import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { AdminPage } from "@/components/AdminPage";
import { LineChart } from "@/components/LineChart";
import { DeltaBadge } from "@/components/DeltaBadge";
import { PeriodSwitcher, type PeriodKey } from "@/components/PeriodSwitcher";
import { api } from "@/lib/api";
import { mapAdminApiError } from "@/lib/errors";
import {
  UNSUBSCRIBE_REASON_LABELS,
  type UnsubscribeReason,
} from "@podecontratar/shared/lib/unsubscribe-reasons";
import type { ApiContract } from "@podecontratar/shared/contract/index";
import type { EndpointOutput } from "@podecontratar/shared/contract/endpoint";

type Stats = EndpointOutput<ApiContract["GET /admin/marketing/unsubscribes"]>;
type Source = Stats["by_source"][number]["source"];

const SOURCE_LABEL: Record<Source, string> = {
  one_click: "Um clique (botão do provedor)",
  confirm_page: "Página de descadastro",
  resend_broadcast: "Broadcast do Resend",
  manual: "Manual",
};

function reasonLabel(reason: UnsubscribeReason | null): string {
  return reason ? UNSUBSCRIBE_REASON_LABELS[reason] : "Sem resposta";
}

function formatInt(n: number): string {
  return n.toLocaleString("pt-BR");
}

function pctOf(n: number, total: number): string {
  return total > 0 ? `${((n / total) * 100).toFixed(1)}%` : "—";
}

function formatDateTime(iso: string): string {
  return new Date(iso.replace(" ", "T") + "Z").toLocaleString("pt-BR", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function AdminMarketingPage() {
  const [period, setPeriod] = useState<PeriodKey>("30d");
  const [data, setData] = useState<Stats | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setData(null);
    setError(null);
    api
      .request("GET /admin/marketing/unsubscribes", { query: { period } })
      .then((d) => {
        if (alive) setData(d);
      })
      .catch((err) => {
        if (alive) setError(mapAdminApiError(err));
      });
    return () => {
      alive = false;
    };
  }, [period]);

  return (
    <AdminPage
      eyebrow="E-mail marketing"
      title="Descadastros"
      description="Quem saiu da lista de campanhas, por onde e por quê. E-mails da conta (login, recibos) não são afetados. Os endereços aparecem mascarados."
      action={<PeriodSwitcher value={period} onChange={setPeriod} />}
    >
      {error ? (
        <div className="rounded-2xl border border-danger/30 bg-danger/10 p-4 text-sm text-text">
          {error}
        </div>
      ) : data === null ? (
        <div className="grid place-items-center rounded-3xl border border-border bg-surface py-20">
          <Loader2 size={20} className="animate-spin text-text-tertiary" aria-hidden />
        </div>
      ) : (
        <Content data={data} />
      )}
    </AdminPage>
  );
}

function Content({ data }: { data: Stats }) {
  const { totals } = data;
  const total = totals.unsubscribes;

  return (
    <div className="flex flex-col gap-10">
      <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat
          label="Descadastros"
          value={formatInt(total)}
          hint={`${formatInt(totals.previous)} no período anterior`}
          badge={<DeltaBadge pct={totals.delta_pct} invertColors />}
        />
        <Stat
          label="Responderam o motivo"
          value={pctOf(totals.with_reason, total)}
          hint={`${formatInt(totals.with_reason)} respostas · ${formatInt(totals.with_note)} com comentário`}
        />
        <Stat
          label="Suprimidos no Resend"
          value={formatInt(totals.resend_synced)}
          hint={`${formatInt(totals.resend_skipped_account_holder)} mantidos por terem conta`}
        />
        <Stat
          label="Sincronização pendente"
          value={formatInt(totals.resend_pending)}
          hint="Nova tentativa no próximo descadastro do mesmo endereço"
        />
      </section>

      <section>
        <div className="mb-3 flex items-center justify-between gap-3">
          <h2 className="text-[11px] font-medium uppercase tracking-[0.2em] text-text-tertiary">
            Descadastros por dia
          </h2>
          <span className="text-sm font-semibold tracking-tight text-text">{formatInt(total)}</span>
        </div>
        <LineChart data={data.series} height={200} />
      </section>

      <section className="grid gap-6 lg:grid-cols-3">
        <Card title="Motivo">
          <Bars
            rows={data.by_reason.map((r) => ({ key: r.reason ?? "none", label: reasonLabel(r.reason), n: r.n }))}
            total={total}
          />
        </Card>
        <Card title="Origem">
          <Bars
            rows={data.by_source.map((r) => ({ key: r.source, label: SOURCE_LABEL[r.source], n: r.n }))}
            total={total}
          />
        </Card>
        <Card title="Campanhas">
          {data.by_campaign.length === 0 ? (
            <p className="text-sm text-text-tertiary">Sem descadastros no período.</p>
          ) : (
            <Bars
              rows={data.by_campaign.map((r) => ({
                key: r.campaign ?? "none",
                label: r.campaign ?? "Sem campanha",
                n: r.n,
                mono: r.campaign !== null,
              }))}
              total={total}
            />
          )}
        </Card>
      </section>

      <section>
        <header className="mb-4 flex items-baseline justify-between">
          <h2 className="text-base font-semibold tracking-tight text-text">Últimos descadastros</h2>
          <p className="text-xs text-text-tertiary">{data.recent.length} mais recentes no período</p>
        </header>
        <div className="overflow-x-auto rounded-2xl border border-border bg-surface">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-surface-alt text-[11px] font-medium uppercase tracking-[0.16em] text-text-tertiary">
                <th className="px-4 py-3 text-left">Data</th>
                <th className="px-4 py-3 text-left">E-mail</th>
                <th className="px-4 py-3 text-left">Origem</th>
                <th className="px-4 py-3 text-left">Campanha</th>
                <th className="px-4 py-3 text-left">Motivo</th>
              </tr>
            </thead>
            <tbody>
              {data.recent.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-text-tertiary">
                    Sem descadastros no período.
                  </td>
                </tr>
              ) : (
                data.recent.map((r) => (
                  <tr key={`${r.email_masked}-${r.created_at}`} className="border-b border-border align-top last:border-0">
                    <td className="whitespace-nowrap px-4 py-3 text-text-secondary">
                      {formatDateTime(r.created_at)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-text">{r.email_masked}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-xs text-text-secondary">
                      {SOURCE_LABEL[r.source]}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-text-secondary">
                      {r.campaign ?? "—"}
                    </td>
                    <td className="min-w-[16rem] px-4 py-3">
                      <p className={r.reason ? "text-text" : "text-text-tertiary"}>{reasonLabel(r.reason)}</p>
                      {r.reason_note && (
                        <p className="mt-1 whitespace-pre-line text-xs text-text-secondary">“{r.reason_note}”</p>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function Bars({
  rows,
  total,
}: {
  rows: Array<{ key: string; label: string; n: number; mono?: boolean }>;
  total: number;
}) {
  return (
    <ul className="flex flex-col gap-3">
      {rows.map((r) => {
        const pct = total > 0 ? (r.n / total) * 100 : 0;
        return (
          <li key={r.key} className="flex flex-col gap-1.5">
            <div className="flex items-baseline justify-between gap-3 text-sm">
              <span className={`truncate ${r.mono ? "font-mono text-xs" : ""} text-text`}>{r.label}</span>
              <span className="shrink-0 text-xs text-text-tertiary">
                <span className="font-medium text-text">{formatInt(r.n)}</span> · {pctOf(r.n, total)}
              </span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-surface-alt">
              <div className="h-full rounded-full bg-accent" style={{ width: `${pct}%` }} />
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function Stat({
  label,
  value,
  hint,
  badge,
}: {
  label: string;
  value: string;
  hint?: string;
  badge?: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-border bg-surface p-5">
      <div className="flex items-center justify-between gap-3">
        <p className="text-[10px] font-medium uppercase tracking-[0.22em] text-text-tertiary">{label}</p>
        {badge}
      </div>
      <p className="mt-3 text-xl font-semibold tracking-tight text-text">{value}</p>
      {hint && <p className="mt-1.5 text-xs text-text-tertiary">{hint}</p>}
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-border bg-surface p-6">
      <h3 className="mb-4 text-sm font-semibold tracking-tight text-text">{title}</h3>
      {children}
    </div>
  );
}
